import { useState } from 'react';

import { ApiError } from '@solar/api-client';
import {
  AS_STATE_LABEL,
  ErrorBox,
  Spinner,
  formatDate,
  formatPhone,
  useAsync,
} from '@solar/ui';

import { api } from '../api';

/** iOS ASListView 필터 4종 파리티 (전체/미배정/배정된/완료된 — Constants.swift AS_TYPE). */
const FILTERS: Array<{ value: number | undefined; label: string }> = [
  { value: undefined, label: '전체' },
  { value: 1, label: '미배정' },
  { value: 2, label: '배정' },
  { value: 3, label: '완료' },
];

function today(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export function AsList({
  meId,
  onOpenOrder,
}: {
  meId: number;
  onOpenOrder: (orderId: number) => void;
}) {
  // iOS 파리티: 기사도 회사 전체 A/S 를 보고, 미배정 건은 본인이 배정받는다.
  const [state, setState] = useState<number | undefined>(undefined);
  const list = useAsync(() => api.listAs({ state, size: 50 }), [state]);

  // 배정받기 (미배정 건 → 본인 self-assign). iOS ASDetail 은 방문예정일 없이는
  // 배정받기를 막았다("방문 예정일을 입력해주세요.") — 같은 규칙을 적용한다.
  const [assigning, setAssigning] = useState<number | null>(null);
  const [dueDate, setDueDate] = useState(today());

  const [completing, setCompleting] = useState<number | null>(null);
  const [memo, setMemo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function assign(id: number) {
    setBusy(true);
    setError(null);
    try {
      await api.assignAs(id, { engineer_id: meId, as_due_date: dueDate });
      setAssigning(null);
      list.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '배정받지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  async function complete(id: number) {
    setBusy(true);
    setError(null);
    try {
      await api.completeAs(id, memo.trim());
      setCompleting(null);
      setMemo('');
      list.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '처리하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="toolbar">
        {FILTERS.map((f) => (
          <button
            key={f.label}
            type="button"
            className={state === f.value ? 'chip active' : 'chip'}
            onClick={() => setState(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {list.loading && <Spinner />}
      {list.error && <ErrorBox message={list.error} onRetry={list.reload} />}
      {list.data && list.data.items.length === 0 && <p className="empty">A/S 건이 없습니다.</p>}

      <ul className="cards">
        {list.data?.items.map((item) => (
          <li key={item.id}>
            <div className="card">
              <div className="card__top">
                <strong>{item.user_name ?? `#${item.build_order_id}`}</strong>
                <span className={`badge a${item.state}`}>
                  {AS_STATE_LABEL[item.state] ?? item.state}
                </span>
              </div>
              <p className="card__addr">{item.addr_full ?? '-'}</p>
              <p className="card__meta">
                접수 {formatDate(item.as_date)} · {formatPhone(item.user_phone)}
                {item.engineer_name && ` · 담당 ${item.engineer_name}`}
                {item.state === 2 && item.as_due_date && ` · 방문 ${formatDate(item.as_due_date)}`}
                {item.state === 3 && item.com_date && ` · 완료 ${formatDate(item.com_date)}`}
              </p>
              {item.etc && <p className="card__note">{item.etc}</p>}

              <div className="card__actions">
                <button type="button" onClick={() => onOpenOrder(item.build_order_id)}>
                  설치건 보기
                </button>
                {item.state === 1 && (
                  <button
                    type="button"
                    className="primary"
                    onClick={() => {
                      setAssigning(item.id);
                      setCompleting(null);
                      setDueDate(today());
                      setError(null);
                    }}
                  >
                    배정받기
                  </button>
                )}
                {item.state === 2 && (
                  <button
                    type="button"
                    className="primary"
                    onClick={() => {
                      setCompleting(item.id);
                      setAssigning(null);
                      setMemo('');
                      setError(null);
                    }}
                  >
                    처리 완료
                  </button>
                )}
              </div>

              {assigning === item.id && (
                <div className="card__form">
                  <label className="input">
                    방문 예정일
                    <input
                      type="date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                    />
                  </label>
                  {error && <p className="error">{error}</p>}
                  <div className="card__actions">
                    <button type="button" onClick={() => setAssigning(null)}>
                      취소
                    </button>
                    <button
                      type="button"
                      className="primary"
                      disabled={busy || dueDate === ''}
                      onClick={() => void assign(item.id)}
                    >
                      {busy ? '배정 중…' : '배정받기'}
                    </button>
                  </div>
                </div>
              )}

              {completing === item.id && (
                <div className="card__form">
                  {/* 레거시는 state / is_complete / com_date 를 각각 다른 요청으로
                      갱신해 중간에 끊기면 '완료인데 완료일 없음' 행이 남았다.
                      신규 API 는 한 번의 호출로 셋을 함께 확정한다. */}
                  <textarea
                    rows={3}
                    value={memo}
                    onChange={(e) => setMemo(e.target.value)}
                    placeholder="처리 내용 (필수)"
                  />
                  {error && <p className="error">{error}</p>}
                  <div className="card__actions">
                    <button type="button" onClick={() => setCompleting(null)}>
                      취소
                    </button>
                    <button
                      type="button"
                      className="primary"
                      disabled={busy || memo.trim().length === 0}
                      onClick={() => void complete(item.id)}
                    >
                      {busy ? '처리 중…' : '완료 저장'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}
